/**
 * Express Checkout — phone number parsing for address mappers
 *
 * Splits stored E.164 phone strings into country prefix + national number
 * for form fields and Saleor address input.
 */

import { COUNTRY_CALLING_CODES } from '@/lib/phonePrefixes/const';

export interface ParsedStoredPhoneNumber {
  phonePrefix: string;
  phone: string;
}

/**
 * Splits a stored E.164-style phone into prefix code and national number for PhoneInput.
 *
 * @example
 * parseStoredPhoneNumber('+39 3469023285', 'IT')
 * // → { phonePrefix: 'IT', phone: '3469023285' }
 */
export function parseStoredPhoneNumber(
  storedPhone: string | null | undefined,
  defaultCountryCode: string
): ParsedStoredPhoneNumber {
  const normalized = storedPhone?.trim() ?? '';

  if (normalized) {
    const sorted = [...COUNTRY_CALLING_CODES].sort((a, b) => b.value.length - a.value.length);
    const match = sorted.find((c) => normalized.startsWith(c.value));

    if (match) {
      return {
        phonePrefix: match.code,
        phone: normalized.slice(match.value.length).trim(),
      };
    }
  }

  const fallback = COUNTRY_CALLING_CODES.find((c) => c.code === defaultCountryCode);

  return {
    phonePrefix: fallback?.code || 'US',
    phone: '',
  };
}
